import React, { useRef } from "react"
import { motion, useInView, useReducedMotion } from "framer-motion"
import LandingNav from "./LandingNav"
import LandingFooter from "./LandingFooter"
import LandingButton from "./LandingButton"
import LandingSparkleBadge from "./LandingSparkleBadge"
import LandingMarquee from "./LandingMarquee"
import LandingExpectSection from "./LandingExpectSection"

const marqueeItems = [
  "Spirit-filled worship",
  "Biblical teaching",
  "Authentic community",
  "Prayer & purpose",
  "Dublin · Cork · Drogheda",
]

type RevealProps = {
  children: React.ReactNode
  className?: string
  delay?: number
}

const Reveal: React.FC<RevealProps> = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: "-60px" })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

const AboutPage: React.FC = () => {
  const prefersReducedMotion = useReducedMotion()

  return (
    <div className="bg-base-100">
      <LandingNav />

      {/* Hero */}
      <section className="relative flex min-h-[44vh] items-end overflow-hidden bg-[#0a0a12] pb-12 pt-24 md:min-h-[48vh] md:pb-14 md:pt-28">
        <img
          src="/community.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-50"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#001a4d]/90 via-[#0d0d0d]/70 to-[#0d0d0d]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(158,126,67,0.25),transparent_50%)]" />

        {!prefersReducedMotion && (
          <motion.div
            className="pointer-events-none absolute -right-20 top-24 h-72 w-72 rounded-full bg-dc-primary-900/30 blur-[100px]"
            animate={{ x: [0, -30, 0], y: [0, 20, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />
        )}

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 md:px-8 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="flex max-w-3xl flex-col gap-4"
          >
            <LandingSparkleBadge label="Who We Are" className="border border-[#9e7e43]/20 bg-[#9e7e43]/10" />
            <h1 className="font-outfit text-3xl font-semibold leading-[1.08] tracking-tight text-white md:text-5xl lg:text-[3.75rem]">
              A Church Where{" "}
              <span className="bg-gradient-to-r from-[#D6E5FF] via-white to-[#9e7e43] bg-clip-text text-transparent">
                You Belong
              </span>
            </h1>
            <p className="max-w-xl font-dm-sans text-base leading-7 text-white/75 md:text-lg">
              Dominion City Ireland is a family of believers raising leaders who transform society through
              faith, community, and purpose.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-[#ebebeb] bg-white py-4">
        <LandingMarquee duration={38} gapClassName="gap-10">
          {marqueeItems.map((item) => (
            <span
              key={item}
              className="whitespace-nowrap font-dm-sans text-sm font-semibold uppercase tracking-[0.22em] text-[#9e7e43]/80"
            >
              {item}
            </span>
          ))}
        </LandingMarquee>
      </section>

      {/* Story */}
      <section className="px-4 py-20 md:px-8 md:py-28 lg:px-20">
        <Reveal className="mx-auto flex max-w-3xl flex-col gap-8">
          <h2 className="font-outfit text-3xl font-semibold tracking-tight text-[#0d0d0d] md:text-4xl lg:text-[2.75rem] lg:leading-tight">
            More than a gathering — a home for the journey
          </h2>
          <div className="flex flex-col gap-6 font-dm-sans text-base leading-7 tracking-wide text-[#2e2e2e]">
            <p>
              We are a community built on faith, love, and purpose. A place where you&apos;ll find room to
              grow spiritually, connect deeply, and experience God in a real and meaningful way.
            </p>
            <p>
              Dominion City is a welcoming community helping people grow in faith, build meaningful
              relationships, and discover purpose in Christ. We are committed to creating a space where
              everyone can encounter God, feel at home, and grow spiritually.
            </p>
          </div>
        </Reveal>
      </section>

      <LandingExpectSection />

      {/* Mission statement */}
      <section className="relative overflow-hidden bg-[#00205c] px-4 py-24 md:px-8 md:py-32 lg:px-20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_0%_100%,rgba(158,126,67,0.2),transparent_55%)]" />
        {!prefersReducedMotion && (
          <motion.div
            className="pointer-events-none absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-dc-primary-900/20 blur-[120px]"
            animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.6, 0.4] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
        )}

        <Reveal className="relative mx-auto max-w-4xl text-center">
          <p className="mb-6 font-dm-sans text-sm font-semibold uppercase tracking-[0.25em] text-[#9e7e43]">
            Our Mission
          </p>
          <blockquote className="font-outfit text-2xl font-medium leading-snug tracking-tight text-white md:text-4xl md:leading-[1.25]">
            &ldquo;Raising leaders that transform society through faith, community, and divine purpose across
            Ireland.&rdquo;
          </blockquote>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="px-4 py-20 md:px-8 lg:px-20">
        <Reveal className="mx-auto flex max-w-4xl flex-col items-center gap-6 rounded-3xl border border-[#ebebeb] bg-white px-6 py-12 text-center shadow-[0_24px_60px_-24px_rgba(0,43,122,0.12)] md:px-12 md:py-16">
          <h2 className="font-outfit text-3xl font-semibold tracking-tight text-[#0d0d0d] md:text-4xl">
            Come as you are. Leave transformed.
          </h2>
          <p className="max-w-lg font-dm-sans text-base leading-7 text-[#2e2e2e]">
            We&apos;d love to meet you. Find a chapter near you and experience Dominion City for yourself.
          </p>
          <div className="flex flex-col gap-4 sm:flex-row">
            <LandingButton href="/#our-locations">Plan a Visit</LandingButton>
            <LandingButton variant="outline-primary" href="/">
              Back to Home
            </LandingButton>
          </div>
        </Reveal>
      </section>

      <LandingFooter />
    </div>
  )
}

export default AboutPage
